import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonPanel = ({ selectedMethodologies, onRemove, onClear, isVisible, onToggle }) => {
  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          variant="primary"
          iconName="GitCompare"
          iconPosition="left"
          onClick={onToggle}
          className="shadow-lg"
        >
          Compare ({selectedMethodologies?.length})
        </Button>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-success';
    if (score >= 6) return 'text-warning';
    return 'text-error';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="GitCompare" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-card-foreground">
              Methodology Comparison
            </h2>
            <span className="text-sm text-muted-foreground">
              ({selectedMethodologies?.length} selected)
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Export Comparison
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onToggle}
            />
          </div>
        </div>

        {/* Comparison Content */}
        <div className="overflow-auto max-h-[calc(90vh-80px)]">
          {selectedMethodologies?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="GitCompare" size={48} className="text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-card-foreground mb-2">
                No Methodologies Selected
              </h3>
              <p className="text-muted-foreground mb-4">
                Select methodologies from the main list to compare them side by side
              </p>
              <Button variant="outline" onClick={onToggle}>
                Back to List
              </Button>
            </div>
          ) : (
            <div className="p-4">
              {/* Comparison Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 font-medium text-card-foreground min-w-32">
                        Criteria
                      </th>
                      {selectedMethodologies?.map((methodology) => (
                        <th key={methodology?.id} className="text-left p-3 min-w-48">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-card-foreground truncate">
                                {methodology?.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {methodology?.registry} v{methodology?.version}
                              </div>
                            </div>
                            <button
                              onClick={() => onRemove(methodology?.id)}
                              className="p-1 hover:bg-muted rounded transition-smooth"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Overall Rating */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Overall Rating</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="flex items-center space-x-2">
                            <span className={`text-lg font-semibold ${getScoreColor(methodology?.rating)}`}>
                              {methodology?.rating}/10
                            </span>
                            <div className="flex">
                              {[...Array(5)]?.map((_, i) => (
                                <Icon
                                  key={i}
                                  name="Star"
                                  size={12}
                                  className={i < methodology?.rating / 2 ? 'text-warning' : 'text-muted-foreground'}
                                />
                              ))}
                            </div>
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Risk Level */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Risk Level</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            methodology?.riskLevel === 'low' ? 'bg-success/10 text-success' :
                            methodology?.riskLevel === 'medium'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                          }`}>
                            <Icon name="AlertTriangle" size={10} className="mr-1" />
                            {methodology?.riskLevel}
                          </span>
                        </td>
                      ))}
                    </tr>

                    {/* Market Adoption */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Market Adoption</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div>
                            <div className="font-medium text-card-foreground">
                              {methodology?.creditsIssued?.toLocaleString()} credits
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {methodology?.projectCount} projects
                            </div>
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Average Price */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Average Price</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="font-medium text-card-foreground">
                            ${methodology?.avgPrice}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            per tCO2e
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Additionality Score */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Additionality</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="flex items-center space-x-2">
                            <div className={`w-12 h-2 bg-muted rounded-full overflow-hidden`}>
                              <div 
                                className={`h-full ${getScoreColor(methodology?.additionalityScore)} bg-current`}
                                style={{ width: `${methodology?.additionalityScore * 10}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">
                              {methodology?.additionalityScore}/10
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Permanence Score */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Permanence</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="flex items-center space-x-2">
                            <div className={`w-12 h-2 bg-muted rounded-full overflow-hidden`}>
                              <div 
                                className={`h-full ${getScoreColor(methodology?.permanenceScore)} bg-current`}
                                style={{ width: `${methodology?.permanenceScore * 10}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">
                              {methodology?.permanenceScore}/10
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Measurability Score */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Measurability</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="flex items-center space-x-2">
                            <div className={`w-12 h-2 bg-muted rounded-full overflow-hidden`}>
                              <div 
                                className={`h-full ${getScoreColor(methodology?.measurabilityScore)} bg-current`}
                                style={{ width: `${methodology?.measurabilityScore * 10}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">
                              {methodology?.measurabilityScore}/10
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Last Updated */}
                    <tr className="border-b border-border">
                      <td className="p-3 font-medium text-card-foreground">Last Updated</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          <div className="text-sm text-card-foreground">
                            {methodology?.lastUpdated}
                          </div>
                        </td>
                      ))}
                    </tr>

                    {/* Scrutiny Flags */}
                    <tr>
                      <td className="p-3 font-medium text-card-foreground">Scrutiny Flags</td>
                      {selectedMethodologies?.map((methodology) => (
                        <td key={methodology?.id} className="p-3">
                          {methodology?.flags && methodology?.flags?.length > 0 ? (
                            <div className="space-y-1">
                              {methodology?.flags?.slice(0, 2)?.map((flag, index) => (
                                <span
                                  key={index}
                                  className="inline-block px-2 py-1 bg-warning/10 text-warning rounded text-xs"
                                >
                                  {flag}
                                </span>
                              ))}
                              {methodology?.flags?.length > 2 && (
                                <div className="text-xs text-muted-foreground">
                                  +{methodology?.flags?.length - 2} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-success">No flags</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={onClear}
                >
                  Clear All
                </Button>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    iconName="FileText"
                    iconPosition="left"
                  >
                    Generate Report
                  </Button>
                  <Button
                    variant="primary"
                    iconName="ShoppingCart"
                    iconPosition="left"
                  >
                    Add to Portfolio
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonPanel;